(() => {
  const initHero = (hero) => {
    if (hero.dataset.cwsMosaicReady === 'true') return;

    const slidesRoot = hero.querySelector('[data-cws-home-hero-slides]');
    const slides = Array.from(hero.querySelectorAll('[data-cws-home-hero-slide]'));
    const sourceFor = (slide) => {
      const image = slide.querySelector('img');

      return image?.currentSrc || image?.src || '';
    };
    const sources = slides.map(sourceFor).filter(Boolean);

    if (!slidesRoot || sources.length < 2) return;

    hero.dataset.cwsMosaicReady = 'true';

    const mosaic = document.createElement('div');
    const duration = 1000;
    const maximumDelay = 1450;
    const firstDelay = 1600;
    const holdDuration = 5200;
    const cache = new Map();
    const cells = [];
    let activeIndex = 0;
    let columns = 0;
    let rows = 0;
    let timer;
    let resizeTimer;
    let isBusy = false;
    let isVisible = true;
    let hasPlayed = false;

    mosaic.className = 'cws-home-hero__mosaic';
    slidesRoot.append(mosaic);

    const imageValue = (source) => `url("${source}")`;
    const setVisibleSlide = (index) => {
      slides.forEach((slide, slideIndex) => {
        slide.toggleAttribute('hidden', slideIndex !== index);
      });
      slidesRoot.style.backgroundImage = imageValue(sources[index]);
    };
    const preload = (source, callback) => {
      if (cache.has(source)) {
        const record = cache.get(source);

        if (record.complete) callback?.(record.loaded);
        else if (callback) record.callbacks.push(callback);

        return;
      }

      const record = { callbacks: callback ? [callback] : [], complete: false, loaded: false };
      const image = new Image();
      const finish = (loaded) => {
        record.complete = true;
        record.loaded = loaded;
        record.callbacks.splice(0).forEach((handler) => handler(loaded));
      };

      cache.set(source, record);
      image.decoding = 'async';
      image.onload = () => finish(true);
      image.onerror = () => finish(false);
      image.src = source;
    };
    const chooseGrid = () => {
      const bounds = slidesRoot.getBoundingClientRect();
      const width = Math.max(bounds.width, 320);
      const height = Math.max(bounds.height, 480);
      const targetSize = width < 640 ? 82 : width < 1100 ? 120 : 150;
      let nextColumns = Math.max(5, Math.min(12, Math.round(width / targetSize)));
      let tileSize = width / nextColumns;
      let nextRows = Math.max(4, Math.min(12, Math.round(height / tileSize)));

      while (nextColumns * nextRows > 60 && nextColumns > 5) {
        nextColumns -= 1;
        tileSize = width / nextColumns;
        nextRows = Math.max(4, Math.min(12, Math.round(height / tileSize)));
      }

      if (nextColumns * nextRows > 60) nextRows = Math.max(4, Math.floor(60 / nextColumns));

      return { columns: nextColumns, rows: nextRows };
    };
    const buildGrid = (force = false) => {
      if (isBusy) return;

      const grid = chooseGrid();

      if (!force && grid.columns === columns && grid.rows === rows) return;

      columns = grid.columns;
      rows = grid.rows;
      cells.length = 0;
      mosaic.replaceChildren();
      mosaic.style.setProperty('--cws-mosaic-columns', columns);
      mosaic.style.setProperty('--cws-mosaic-rows', rows);
      const fragment = document.createDocumentFragment();
      const currentImage = imageValue(sources[activeIndex]);

      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const cell = document.createElement('div');
          const card = document.createElement('div');
          const frontFace = document.createElement('div');
          const backFace = document.createElement('div');
          const frontImage = document.createElement('div');
          const backImage = document.createElement('div');

          cell.className = 'cws-home-hero__mosaic-cell';
          card.className = 'cws-home-hero__mosaic-card';
          frontFace.className = 'cws-home-hero__mosaic-face cws-home-hero__mosaic-face--front';
          backFace.className = 'cws-home-hero__mosaic-face cws-home-hero__mosaic-face--back';
          frontImage.className = 'cws-home-hero__mosaic-image';
          backImage.className = 'cws-home-hero__mosaic-image';
          cell.style.setProperty('--cws-mosaic-column', column);
          cell.style.setProperty('--cws-mosaic-row', row);
          frontImage.style.backgroundImage = currentImage;
          backImage.style.backgroundImage = currentImage;
          frontFace.append(frontImage);
          backFace.append(backImage);
          card.append(frontFace, backFace);
          cell.append(card);
          fragment.append(cell);
          cells.push({ backImage, cell, column, frontImage, row });
        }
      }

      mosaic.append(fragment);
      setVisibleSlide(activeIndex);
    };
    const clearTimer = () => {
      if (!timer) return;

      window.clearTimeout(timer);
      timer = undefined;
    };
    const schedule = (delay) => {
      if (timer || !isVisible || document.hidden) return;

      timer = window.setTimeout(() => {
        timer = undefined;
        transition();
      }, delay);
    };
    const transition = () => {
      if (document.hidden || !isVisible || isBusy || !document.body.contains(hero)) return;

      isBusy = true;
      const nextIndex = (activeIndex + 1) % sources.length;
      const nextImage = imageValue(sources[nextIndex]);

      preload(sources[nextIndex], (loaded) => {
        if (!loaded || document.hidden || !isVisible || !document.body.contains(hero)) {
          isBusy = false;
          schedule(holdDuration);
          return;
        }

        cells.forEach((cell) => {
          const horizontalProgress = columns > 1 ? cell.column / (columns - 1) : 0;
          const verticalProgress = rows > 1 ? cell.row / (rows - 1) : 0;
          const delay = ((horizontalProgress + verticalProgress) * 0.5) * maximumDelay;

          cell.backImage.style.backgroundImage = nextImage;
          cell.cell.style.setProperty('--cws-mosaic-delay', `${(delay / 1000).toFixed(3)}s`);
        });

        mosaic.classList.add('is-animating');
        void mosaic.offsetWidth;
        cells.forEach((cell) => cell.cell.classList.add('is-flipping'));

        window.setTimeout(() => {
          setVisibleSlide(nextIndex);
          cells.forEach((cell) => {
            cell.frontImage.style.backgroundImage = nextImage;
            cell.cell.classList.remove('is-flipping');
          });
          mosaic.classList.remove('is-animating');
          activeIndex = nextIndex;
          isBusy = false;
          hasPlayed = true;
          preload(sources[(activeIndex + 1) % sources.length]);
          schedule(holdDuration);
        }, duration + maximumDelay + 90);
      });
    };
    const play = () => schedule(hasPlayed ? holdDuration : firstDelay);
    const scheduleGrid = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (isBusy) {
          scheduleGrid();
          return;
        }
        buildGrid();
      }, 180);
    };

    setVisibleSlide(activeIndex);
    buildGrid(true);
    preload(sources[activeIndex]);
    preload(sources[(activeIndex + 1) % sources.length]);
    play();

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearTimer();
      else play();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        isVisible = Boolean(entries[0]?.isIntersecting);
        if (isVisible) play();
        else clearTimer();
      }, { rootMargin: '120px 0px' }).observe(hero);
    }

    if ('ResizeObserver' in window) new ResizeObserver(scheduleGrid).observe(slidesRoot);
    else window.addEventListener('resize', scheduleGrid, { passive: true });
  };

  const initAll = (root) => {
    if (!root) return;

    if (root.matches?.('[data-cws-home-hero]')) initHero(root);

    root.querySelectorAll?.('[data-cws-home-hero]').forEach(initHero);
  };

  initAll(document);

  // The theme editor replaces the section markup and dispatches this event. Without
  // re-initialising here the new hero gets no script at all, so the mosaic is never
  // built and every slide except the first stays hidden. The cwsMosaicReady flag on
  // each element keeps a re-run from initialising the same hero twice.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
